import React, { useState, useMemo } from "react";
import {
  ChevronDown,
  Building2,
  Globe,
  Building,
  MapPin,
  Users,
  UserCheck,
  Layers,
} from "lucide-react";
import { Site, Organization } from "../types";

export interface SiteWithHierarchy {
  site: Site;
  orgName: string;
  orgId: string;
  level: number;
  hasChildren: boolean;
  isExpanded?: boolean;
}

interface OrganizationSiteFilterProps {
  organizations: Organization[];
  selectedOrganization?: Organization | null;
  selectedSite?: Site | null;
  onOrganizationSelect: (org: Organization | null) => void;
  onSiteSelect: (site: Site | null) => void;
  showAllSitesOption?: boolean;
  isAllSitesSelected?: boolean;
  onAllSitesSelect?: () => void;
  className?: string;
}

const OrganizationSiteFilter: React.FC<OrganizationSiteFilterProps> = ({
  organizations,
  selectedOrganization,
  selectedSite,
  onOrganizationSelect,
  onSiteSelect,
  showAllSitesOption = true,
  isAllSitesSelected = false,
  onAllSitesSelect,
  className = "",
}) => {
  const [isOrgOpen, setIsOrgOpen] = useState(false);
  const [isSiteOpen, setIsSiteOpen] = useState(false);
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());

  // Build hierarchical site structure
  const hierarchicalSites = useMemo(() => {
    if (!selectedOrganization) return [];

    const buildHierarchy = (
      sites: Site[],
      orgName: string,
      orgId: string,
      level: number = 0
    ): SiteWithHierarchy[] => {
      const result: SiteWithHierarchy[] = [];

      sites.forEach((site) => {
        const hasChildren = !!(site.children && site.children.length > 0);
        result.push({
          site,
          orgName,
          orgId,
          level,
          hasChildren,
          isExpanded: expandedSites.has(site.id),
        });

        // Add children if expanded
        if (hasChildren && expandedSites.has(site.id)) {
          result.push(
            ...buildHierarchy(site.children!, orgName, orgId, level + 1)
          );
        }
      });

      return result;
    };

    return buildHierarchy(
      selectedOrganization.sites,
      selectedOrganization.name,
      selectedOrganization.id
    );
  }, [selectedOrganization, expandedSites]);

  // Get all sites count for selected organization
  const allSitesCount = useMemo(() => {
    if (!selectedOrganization) return 0;

    const countSites = (sites: Site[]): number => {
      let count = 0;
      sites.forEach((site) => {
        count += 1;
        if (site.children) {
          count += countSites(site.children);
        }
      });
      return count;
    };

    return countSites(selectedOrganization.sites);
  }, [selectedOrganization]);

  const getSiteIcon = (type: string) => {
    switch (type) {
      case "region":
        return <Globe className="w-4 h-4 text-blue-600" />;
      case "building":
        return <Building className="w-4 h-4 text-green-600" />;
      case "location":
        return <MapPin className="w-4 h-4 text-orange-600" />;
      default:
        return <Building2 className="w-4 h-4 text-purple-600" />;
    }
  };

  const handleOrgSelect = (org: Organization | null) => {
    onOrganizationSelect(org);
    onSiteSelect(null); // Clear site selection when org changes

    // Automatically select "All Sites" when an organization is chosen
    if (org && !!showAllSitesOption && onAllSitesSelect) {
      onAllSitesSelect();
    }

    setExpandedSites(new Set()); // Reset expanded state
    setIsOrgOpen(false);
  };

  const handleSiteSelect = (site: Site | null) => {
    onSiteSelect(site);
    setIsSiteOpen(false);
  };

  const handleAllSitesSelect = () => {
    onSiteSelect(null); // Clear specific site selection
    if (onAllSitesSelect) {
      onAllSitesSelect();
    }
    setIsSiteOpen(false);
  };

  const toggleSiteExpansion = (siteId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedSites);
    if (expandedSites.has(siteId)) {
      newExpanded.delete(siteId);
    } else {
      newExpanded.add(siteId);
    }
    setExpandedSites(newExpanded);
  };

  return (
    <div className={`relative ${className}`}>
      <div className="flex gap-2">
        {/* Organization Dropdown */}
        <div className="relative">
          <button
            onClick={() => setIsOrgOpen(!isOrgOpen)}
            className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between min-w-[300px]"
          >
            <div className="flex items-center space-x-2">
              {selectedOrganization ? (
                <>
                  <UserCheck className="w-4 h-4 text-blue-600" />
                  <span className="font-medium">
                    {selectedOrganization.name}
                  </span>
                </>
              ) : (
                <>
                  <Users className="w-4 h-4 text-gray-600" />
                  <span className="text-gray-500">All Customers</span>
                </>
              )}
            </div>
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform ${
                isOrgOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Organization Dropdown Menu */}
          {isOrgOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
              <div className="p-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-700">
                    Customers
                  </span>
                  <button
                    onClick={() => handleOrgSelect(null)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Clear
                  </button>
                </div>

                <div className="space-y-1">
                  <button
                    onClick={() => handleOrgSelect(null)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-50 flex items-center space-x-2 ${
                      !selectedOrganization
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                  >
                    <Users className="w-4 h-4 text-gray-600" />
                    <span className="font-medium">All Customers</span>
                  </button>

                  {organizations.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => handleOrgSelect(org)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-50 flex items-center space-x-2 ${
                        selectedOrganization?.id === org.id
                          ? "bg-blue-50 text-blue-700"
                          : "text-gray-700"
                      }`}
                    >
                      <UserCheck className="w-4 h-4 text-blue-600" />
                      <span className="font-medium">{org.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Site Dropdown */}
        <div className="relative flex-1">
          <button
            onClick={() => setIsSiteOpen(!isSiteOpen)}
            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex items-center justify-between"
            disabled={!selectedOrganization}
          >
            <div className="flex items-center space-x-2">
              {isAllSitesSelected && selectedOrganization ? (
                <>
                  <Layers className="w-4 h-4 text-indigo-600" />
                  <span className="font-medium">
                    All Sites ({allSitesCount})
                  </span>
                  <span className="text-xs text-gray-500">
                    ({selectedOrganization.name})
                  </span>
                </>
              ) : selectedSite ? (
                <>
                  {getSiteIcon(selectedSite.type)}
                  <span className="font-medium">{selectedSite.name}</span>
                  <span className="text-xs text-gray-500">
                    ({selectedOrganization?.name})
                  </span>
                </>
              ) : selectedOrganization ? (
                <span className="text-gray-500">
                  Select a site or "All Sites"...
                </span>
              ) : (
                <span className="text-gray-400">Select customer first</span>
              )}
            </div>
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform ${
                isSiteOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Site Dropdown Menu */}
          {isSiteOpen && selectedOrganization && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
              <div className="p-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-700">
                    Sites in {selectedOrganization.name}
                  </span>
                  <button
                    onClick={() => onSiteSelect(null)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Clear
                  </button>
                </div>

                {hierarchicalSites.length === 0 ? (
                  <div className="text-sm text-gray-500 py-2 text-center">
                    No sites available
                  </div>
                ) : (
                  <div className="space-y-1">
                    {/* Add "All Sites" option */}
                    {showAllSitesOption && (
                      <button
                        onClick={handleAllSitesSelect}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-50 flex items-center space-x-2 border-b border-gray-100 mb-1 ${
                          isAllSitesSelected
                            ? "bg-indigo-50 text-indigo-700"
                            : "text-gray-700"
                        }`}
                      >
                        <Layers className="w-4 h-4 text-indigo-600" />
                        <span className="font-medium">
                          All Sites ({allSitesCount})
                        </span>
                        <span className="text-xs text-gray-500">
                          (Show all sites)
                        </span>
                      </button>
                    )}

                    {/* Hierarchical site list */}
                    {hierarchicalSites.map(
                      ({ site, level, hasChildren, isExpanded }) => (
                        <div key={site.id} className="space-y-1">
                          <button
                            onClick={() => handleSiteSelect(site)}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-gray-50 flex items-center space-x-2 ${
                              selectedSite?.id === site.id &&
                              !isAllSitesSelected
                                ? "bg-blue-50 text-blue-700"
                                : "text-gray-700"
                            }`}
                            style={{ paddingLeft: `${12 + level * 16}px` }}
                          >
                            {hasChildren && (
                              <button
                                onClick={(e) => toggleSiteExpansion(site.id, e)}
                                className="p-0.5 hover:bg-gray-200 rounded mr-1"
                              >
                                <ChevronDown
                                  className={`w-3 h-3 transition-transform ${
                                    isExpanded ? "rotate-0" : "-rotate-90"
                                  }`}
                                />
                              </button>
                            )}
                            {!hasChildren && level > 0 && (
                              <div className="w-4 h-3" /> // Spacing for alignment
                            )}
                            {getSiteIcon(site.type)}
                            <span className="font-medium">{site.name}</span>
                            <span className="text-xs text-gray-500">
                              ({site.deviceCount || 0} devices)
                            </span>
                          </button>
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close */}
      {(isSiteOpen || isOrgOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setIsSiteOpen(false);
            setIsOrgOpen(false);
          }}
        />
      )}
      {selectedOrganization && !selectedSite && !isAllSitesSelected && (
        <div className="mt-2 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-md p-2">
          💡 Select a specific site or choose "All Sites" to view device data
          for {selectedOrganization.name}
        </div>
      )}
    </div>
  );
};

export default OrganizationSiteFilter;
